import { Dispatch, SetStateAction, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import classNames from 'classnames';
import {
  createChart,
  HistogramData,
  HistogramSeries,
  IChartApi,
  IRange,
  ISeriesApi,
  LineData,
  LineSeries,
  Time,
  UTCTimestamp,
  WhitespaceData
} from 'lightweight-charts';
import { useOnClickOutside } from 'usehooks-ts';

import { ElementNames, isTabletOrMobileDevice } from 'constants/app';
import { marketGraphsBranding } from 'constants/branding';
import { MARKET_GRAPH_LINE_CHART_SELECTION_BASE_URL } from 'constants/locations';
import {
  ChartSelections,
  DESKTOP_STATIC_ELEMENTS_HEIGHT,
  INTERVAL_1_DAY,
  INTERVAL_2_HOURS,
  INTERVAL_5_MIN,
  INTERVAL_15_SEC,
  IntervalsPeriodsMap,
  LINE_SERIES_CHANCE_PRICE_FORMAT,
  LINE_SERIES_ODDS_PRICE_FORMAT,
  LINE_SERIES_OPTIONS,
  MARKET_GRAPH_LIQUIDITY_TABLE_TAB_INDEX,
  MARKET_GRAPH_SELECTION_PARAM_NAME,
  MarketGraphChartDataGroupingPeriods,
  MarketGraphChartFilters,
  MIN_DESKTOP_WINDOW_HEIGHT,
  ONE_HOUR_IN_MILLISECONDS,
  ONE_MONTH_IN_MILLISECONDS,
  PeriodsIntervalsMap,
  PRICE_SCALE_OPTIONS
} from 'constants/marketGraph';
import { useMarketUnits } from 'hooks/useMarketUnits';
import {
  getCurrency,
  getDisplayCurrencySymbol,
  getIndianNumberSystemEnabled,
  getIsAmericanDateFormatEnabled,
  getIsCurrencySymbolAfterAmount,
  getIsMarketGraphsDarkThemeEnabled,
  getIsMarketGraphsSimplifiedViewEnabled,
  getMarketGraphIsSmallScreenView,
  getReplaceCurrencySymbolForMatchedAndTooltips,
  getWindowHeight
} from 'redux/modules/appConfigs/selectors';
import { getElementHeightByName } from 'redux/modules/appSettings/selectors';
import {
  getMarketDefinitionInPlayStatus,
  getMarketDefinitionMarketUnit
} from 'redux/modules/marketDefinitions/selectors';
import {
  getMarketGraphChartDataPoints,
  getMarketGraphChartIsUpdated,
  getMarketGraphChartUpdatedPoints,
  getMarketGraphIsMarketBettingType,
  getMarketGraphMarketStartTime
} from 'redux/modules/marketGraph/selectors';
import { getPrecisionType } from 'redux/modules/user/selectors';
import {
  MarketGraphChartDataGroupingPeriod,
  MarketGraphChartFilter,
  MarketGraphChartSelection
} from 'types/marketGraph';
import { getFormatValueWithCurrency } from 'utils/currency';
import {
  concatExistAndPrevChartData,
  convertLineChartDataForSelection,
  getChartInterval,
  getChartOptions,
  getChartTimeValue,
  getLineAndHistogramData,
  getLineChartDataValue,
  groupChartDataByInterval
} from 'utils/marketGraph';

import styles from './MarketGraphChartContent.module.scss';

export default function MarketGraphChartContent({
  setIsChartError
}: {
  setIsChartError: Dispatch<SetStateAction<boolean>>;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    marketId = '',
    selectionId,
    handicap
  } = useParams<{ marketId: string; selectionId: string; handicap: string }>();

  const isSmallScreenView = useSelector(getMarketGraphIsSmallScreenView);
  const chartData = useSelector(getMarketGraphChartDataPoints);
  const americanDateFormatEnabled = useSelector(getIsAmericanDateFormatEnabled);
  const displayCurrencySymbol = useSelector(getDisplayCurrencySymbol);
  const currencySymbolAfterAmount = useSelector(getIsCurrencySymbolAfterAmount);
  const indianNumberSystemEnabled = useSelector(getIndianNumberSystemEnabled);
  const replaceCurrencySymbolForMatchedAndTooltips = useSelector(getReplaceCurrencySymbolForMatchedAndTooltips);
  const precisionType = useSelector(getPrecisionType);
  const currency = useSelector(getCurrency);
  const windowHeight = useSelector(getWindowHeight);
  const headerHeight = useSelector(getElementHeightByName(ElementNames.MARKET_GRAPH_HEADER));
  const totalMatchedBlockHeight = useSelector(getElementHeightByName(ElementNames.MARKET_GRAPH_TOTAL_MATCHED_BLOCK));
  const marketStartTime = useSelector(getMarketGraphMarketStartTime);
  const isChartDataUpdated = useSelector(getMarketGraphChartIsUpdated);
  const chartUpdatedPoints = useSelector(getMarketGraphChartUpdatedPoints);
  const isMarketInPlay = useSelector(getMarketDefinitionInPlayStatus(marketId));
  const isSimplifiedView = useSelector(getIsMarketGraphsSimplifiedViewEnabled);
  const isDarkTheme = useSelector(getIsMarketGraphsDarkThemeEnabled);
  const isLineMarket = useSelector(getMarketGraphIsMarketBettingType);
  const marketUnit = useSelector(getMarketDefinitionMarketUnit(marketId || ''));

  const [selectedFilter, setSelectedFilter] = useState<MarketGraphChartFilter>(MarketGraphChartFilters.all);
  const [isOpenedDropdown, setIsOpenedDropdown] = useState(false);
  const [isChartZoomed, setIsChartZoomed] = useState(false);
  const [isToolbarPanLeftDisabled, setIsToolbarPanLeftDisabled] = useState(false);
  const [isToolbarPanRightDisabled, setIsToolbarPanRightDisabled] = useState(false);

  const dropdownRef = useRef<HTMLDivElement>(null);
  const chartContainerRef = useRef<HTMLDivElement | null>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const volumeSeriesRef = useRef<ISeriesApi<'Histogram'> | null>(null);
  const lineSeriesRef = useRef<ISeriesApi<'Line'> | null>(null);
  const oddsLegendColorRef = useRef<HTMLDivElement>(null);
  const volumeLegendColorRef = useRef<HTMLDivElement>(null);
  const filterSelectionInProcess = useRef(false);
  const initialRange = useRef<IRange<Time> | null>(null);
  const isChartZoomedRef = useRef(isChartZoomed);
  const inPlayMarkerLineRef = useRef<HTMLDivElement | null>(null);
  const inPlayMarkerTextRef = useRef<HTMLParagraphElement | null>(null);
  const selectedFilterRef = useRef(selectedFilter);
  const currentInterval = useRef(0);
  const windowWidthRef = useRef(window.innerWidth);
  const allTimePeriod = useRef<MarketGraphChartDataGroupingPeriod>(MarketGraphChartDataGroupingPeriods.day1);
  const periodsData = useRef<
    Record<
      MarketGraphChartDataGroupingPeriod,
      { oddsData: (WhitespaceData | LineData)[]; volumeData: (WhitespaceData | HistogramData)[] }
    >
  >({
    [MarketGraphChartDataGroupingPeriods.sec15]: { oddsData: [], volumeData: [] },
    [MarketGraphChartDataGroupingPeriods.min5]: { oddsData: [], volumeData: [] },
    [MarketGraphChartDataGroupingPeriods.hour2]: { oddsData: [], volumeData: [] },
    [MarketGraphChartDataGroupingPeriods.day1]: { oddsData: [], volumeData: [] }
  });
  const isResetClicked = useRef(false);
  const isFirstRender = useRef(true);
  const isSimplifiedViewChanged = useRef(false); // needs to detect if chart was zoomed in subscribeVisibleTimeRangeChange
  const isToolbarPanLeftDisabledRef = useRef(isToolbarPanLeftDisabled);
  const isToolbarPanRightDisabledRef = useRef(isToolbarPanRightDisabled);

  useOnClickOutside(dropdownRef, () => setIsOpenedDropdown(false));
  const marketUnitTranslated = useMarketUnits(marketUnit || 'points');

  const chartSelection =
    (searchParams.get(MARKET_GRAPH_SELECTION_PARAM_NAME) as MarketGraphChartSelection) || ChartSelections.odds;
  const minWindowHeight = windowHeight < MIN_DESKTOP_WINDOW_HEIGHT ? MIN_DESKTOP_WINDOW_HEIGHT : windowHeight;
  const isChanceView = chartSelection === ChartSelections.chance;
  const isMobile = isSmallScreenView || isTabletOrMobileDevice;
  const lastTimestamp = chartData?.length ? chartData[chartData.length - 1].timestamp : 0;

  const filters = useMemo(() => {
    return [
      { label: t('marketGraph.labels.allTime'), isVisible: true, value: MarketGraphChartFilters.all },
      { label: t('marketGraph.labels.24hours'), isVisible: true, value: MarketGraphChartFilters.day },
      { label: t('marketGraph.labels.hour'), isVisible: true, value: MarketGraphChartFilters.hour },
      { label: t('market.inPlay'), isVisible: isMarketInPlay, value: MarketGraphChartFilters.inPlay }
    ].filter(({ isVisible }) => isVisible);
  }, [isMarketInPlay]);

  const chartSelections = [
    { label: t('account.mybets.labels.odds'), value: ChartSelections.odds },
    { label: t('marketGraph.labels.chance'), value: ChartSelections.chance }
  ];

  const updateInPlayMarkerPosition = () => {
    if (inPlayMarkerTextRef.current && chartRef.current && inPlayMarkerLineRef.current && marketStartTime) {
      const inPlayTime = (marketStartTime / 1000) as UTCTimestamp;
      let timeCoordinate = chartRef.current.timeScale().timeToCoordinate(inPlayTime);

      if (timeCoordinate === null) {
        const closestInPlayTimeTimestamp = periodsData.current[
          IntervalsPeriodsMap[currentInterval.current]
        ].oddsData.find(({ time }) => time > inPlayTime)?.time;

        if (closestInPlayTimeTimestamp) {
          timeCoordinate = chartRef.current.timeScale().timeToCoordinate(closestInPlayTimeTimestamp);
        } else {
          const oddsData = periodsData.current[IntervalsPeriodsMap[currentInterval.current]].oddsData;

          timeCoordinate = chartRef.current.timeScale().timeToCoordinate(oddsData[oddsData.length - 1].time);
        }
      }

      inPlayMarkerTextRef.current.style.left = `${timeCoordinate}px`;
      inPlayMarkerLineRef.current.style.left = `${timeCoordinate}px`;
    }
  };

  useEffect(() => {
    // This useEffect should be before chart was created to avoid extra changing chart options
    if (chartRef.current) {
      chartRef.current.applyOptions({
        rightPriceScale: {
          visible: !isSimplifiedView
        }
      });
    }
  }, [isSimplifiedView]);

  useEffect(() => {
    if (
      chartUpdatedPoints.length &&
      volumeSeriesRef.current &&
      lineSeriesRef.current &&
      isChartDataUpdated &&
      currentInterval.current
    ) {
      const newData = groupChartDataByInterval({
        data: chartUpdatedPoints,
        interval: currentInterval.current
      });
      const newLineDataByCurrentInterval: (WhitespaceData | LineData)[] = [];
      const newHistogramDataByCurrentInterval: (WhitespaceData | HistogramData)[] = [];

      if (newData.length) {
        newData.forEach(newPoint => {
          const time = getChartTimeValue(newPoint.timestamp);
          const lineDataValue = getLineChartDataValue({ value: newPoint.value, selection: chartSelection });

          newLineDataByCurrentInterval.push({ time, value: lineDataValue });
          newHistogramDataByCurrentInterval.push({ time, value: newPoint.volume });

          try {
            lineSeriesRef.current!.update({ time, value: lineDataValue });
            volumeSeriesRef.current!.update({ time, value: newPoint.volume });
          } catch (error: unknown) {
            setIsChartError(true);
            // eslint-disable-next-line no-console
            console.log('update chart error', {
              error,
              periodsData: periodsData.current,
              newPoint,
              lineDataValue,
              chartData,
              chartUpdatedPoints,
              time,
              allTimePeriod: allTimePeriod.current,
              currentInterval: currentInterval.current
            });
          }
        });
      }

      const { period } = getChartInterval({
        startTimestamp: chartData[0].timestamp,
        endTimestamp: chartUpdatedPoints[chartUpdatedPoints.length - 1].timestamp
      });

      if (period !== allTimePeriod.current) {
        allTimePeriod.current = period;
      }

      const chartDataByCurrentInterval = {
        lineData: newLineDataByCurrentInterval,
        histogramData: newHistogramDataByCurrentInterval
      };
      const isDataForDay1 = IntervalsPeriodsMap[currentInterval.current] === MarketGraphChartDataGroupingPeriods.day1;
      const isDataForHour2 = IntervalsPeriodsMap[currentInterval.current] === MarketGraphChartDataGroupingPeriods.hour2;
      const isDataForMin5 = IntervalsPeriodsMap[currentInterval.current] === MarketGraphChartDataGroupingPeriods.min5;
      const isDataForSec15 = IntervalsPeriodsMap[currentInterval.current] === MarketGraphChartDataGroupingPeriods.sec15;

      // region store new points for all periods
      if (allTimePeriod.current === MarketGraphChartDataGroupingPeriods.day1) {
        const day1Data = isDataForDay1
          ? chartDataByCurrentInterval
          : getLineAndHistogramData({
              chartData: chartUpdatedPoints,
              chartSelection,
              interval: INTERVAL_1_DAY
            });
        const hour2Data = isDataForDay1
          ? chartDataByCurrentInterval
          : getLineAndHistogramData({
              chartData: chartUpdatedPoints,
              chartSelection,
              interval: INTERVAL_2_HOURS
            });
        const min5Data = isDataForMin5
          ? chartDataByCurrentInterval
          : getLineAndHistogramData({
              chartData: chartUpdatedPoints,
              chartSelection,
              interval: INTERVAL_5_MIN
            });
        const sec15Data = isDataForSec15
          ? chartDataByCurrentInterval
          : getLineAndHistogramData({
              chartData: chartUpdatedPoints,
              chartSelection,
              interval: INTERVAL_15_SEC
            });

        const newConcatenatedDay1Data = concatExistAndPrevChartData({
          prevLineChartData: periodsData.current[MarketGraphChartDataGroupingPeriods.day1].oddsData,
          prevHistogramChartData: periodsData.current[MarketGraphChartDataGroupingPeriods.day1].volumeData,
          newLineChartData: day1Data.lineData,
          newHistogramChartData: day1Data.histogramData
        });
        const newConcatenatedHour2Data = concatExistAndPrevChartData({
          prevLineChartData: periodsData.current[MarketGraphChartDataGroupingPeriods.hour2].oddsData,
          prevHistogramChartData: periodsData.current[MarketGraphChartDataGroupingPeriods.hour2].volumeData,
          newLineChartData: hour2Data.lineData,
          newHistogramChartData: hour2Data.histogramData
        });
        const newConcatenatedMin5Data = concatExistAndPrevChartData({
          prevLineChartData: periodsData.current[MarketGraphChartDataGroupingPeriods.min5].oddsData,
          prevHistogramChartData: periodsData.current[MarketGraphChartDataGroupingPeriods.min5].volumeData,
          newLineChartData: min5Data.lineData,
          newHistogramChartData: min5Data.histogramData
        });
        const newConcatenatedSec15Data = concatExistAndPrevChartData({
          prevLineChartData: periodsData.current[MarketGraphChartDataGroupingPeriods.sec15].oddsData,
          prevHistogramChartData: periodsData.current[MarketGraphChartDataGroupingPeriods.sec15].volumeData,
          newLineChartData: sec15Data.lineData,
          newHistogramChartData: sec15Data.histogramData
        });

        periodsData.current = {
          [MarketGraphChartDataGroupingPeriods.day1]: {
            oddsData: newConcatenatedDay1Data.lineData,
            volumeData: newConcatenatedDay1Data.histogramData
          },
          [MarketGraphChartDataGroupingPeriods.hour2]: {
            oddsData: newConcatenatedHour2Data.lineData,
            volumeData: newConcatenatedHour2Data.histogramData
          },
          [MarketGraphChartDataGroupingPeriods.min5]: {
            oddsData: newConcatenatedMin5Data.lineData,
            volumeData: newConcatenatedMin5Data.histogramData
          },
          [MarketGraphChartDataGroupingPeriods.sec15]: {
            oddsData: newConcatenatedSec15Data.lineData,
            volumeData: newConcatenatedSec15Data.histogramData
          }
        };
      } else if (allTimePeriod.current === MarketGraphChartDataGroupingPeriods.hour2) {
        const hour2Data = isDataForHour2
          ? chartDataByCurrentInterval
          : getLineAndHistogramData({
              chartData: chartUpdatedPoints,
              chartSelection,
              interval: INTERVAL_2_HOURS
            });
        const min5Data = isDataForMin5
          ? chartDataByCurrentInterval
          : getLineAndHistogramData({
              chartData: chartUpdatedPoints,
              chartSelection,
              interval: INTERVAL_5_MIN
            });
        const sec15Data = isDataForSec15
          ? chartDataByCurrentInterval
          : getLineAndHistogramData({
              chartData: chartUpdatedPoints,
              chartSelection,
              interval: INTERVAL_15_SEC
            });

        const newConcatenatedHour2Data = concatExistAndPrevChartData({
          prevLineChartData: periodsData.current[MarketGraphChartDataGroupingPeriods.hour2].oddsData,
          prevHistogramChartData: periodsData.current[MarketGraphChartDataGroupingPeriods.hour2].volumeData,
          newLineChartData: hour2Data.lineData,
          newHistogramChartData: hour2Data.histogramData
        });
        const newConcatenatedMin5Data = concatExistAndPrevChartData({
          prevLineChartData: periodsData.current[MarketGraphChartDataGroupingPeriods.min5].oddsData,
          prevHistogramChartData: periodsData.current[MarketGraphChartDataGroupingPeriods.min5].volumeData,
          newLineChartData: min5Data.lineData,
          newHistogramChartData: min5Data.histogramData
        });
        const newConcatenatedSec15Data = concatExistAndPrevChartData({
          prevLineChartData: periodsData.current[MarketGraphChartDataGroupingPeriods.sec15].oddsData,
          prevHistogramChartData: periodsData.current[MarketGraphChartDataGroupingPeriods.sec15].volumeData,
          newLineChartData: sec15Data.lineData,
          newHistogramChartData: sec15Data.histogramData
        });

        periodsData.current = {
          ...periodsData.current,
          [MarketGraphChartDataGroupingPeriods.hour2]: {
            oddsData: newConcatenatedHour2Data.lineData,
            volumeData: newConcatenatedHour2Data.histogramData
          },
          [MarketGraphChartDataGroupingPeriods.min5]: {
            oddsData: newConcatenatedMin5Data.lineData,
            volumeData: newConcatenatedMin5Data.histogramData
          },
          [MarketGraphChartDataGroupingPeriods.sec15]: {
            oddsData: newConcatenatedSec15Data.lineData,
            volumeData: newConcatenatedSec15Data.histogramData
          }
        };
      } else if (allTimePeriod.current === MarketGraphChartDataGroupingPeriods.min5) {
        const min5Data = isDataForMin5
          ? chartDataByCurrentInterval
          : getLineAndHistogramData({
              chartData: chartUpdatedPoints,
              chartSelection,
              interval: INTERVAL_5_MIN
            });
        const sec15Data = isDataForSec15
          ? chartDataByCurrentInterval
          : getLineAndHistogramData({
              chartData: chartUpdatedPoints,
              chartSelection,
              interval: INTERVAL_15_SEC
            });

        const newConcatenatedMin5Data = concatExistAndPrevChartData({
          prevLineChartData: periodsData.current[MarketGraphChartDataGroupingPeriods.min5].oddsData,
          prevHistogramChartData: periodsData.current[MarketGraphChartDataGroupingPeriods.min5].volumeData,
          newLineChartData: min5Data.lineData,
          newHistogramChartData: min5Data.histogramData
        });
        const newConcatenatedSec15Data = concatExistAndPrevChartData({
          prevLineChartData: periodsData.current[MarketGraphChartDataGroupingPeriods.sec15].oddsData,
          prevHistogramChartData: periodsData.current[MarketGraphChartDataGroupingPeriods.sec15].volumeData,
          newLineChartData: sec15Data.lineData,
          newHistogramChartData: sec15Data.histogramData
        });

        periodsData.current = {
          ...periodsData.current,
          [MarketGraphChartDataGroupingPeriods.min5]: {
            oddsData: newConcatenatedMin5Data.lineData,
            volumeData: newConcatenatedMin5Data.histogramData
          },
          [MarketGraphChartDataGroupingPeriods.sec15]: {
            oddsData: newConcatenatedSec15Data.lineData,
            volumeData: newConcatenatedSec15Data.histogramData
          }
        };
      } else {
        const sec15Data = isDataForSec15
          ? chartDataByCurrentInterval
          : getLineAndHistogramData({
              chartData: chartUpdatedPoints,
              chartSelection,
              interval: INTERVAL_15_SEC
            });

        const newConcatenatedSec15Data = concatExistAndPrevChartData({
          prevLineChartData: periodsData.current[MarketGraphChartDataGroupingPeriods.sec15].oddsData,
          prevHistogramChartData: periodsData.current[MarketGraphChartDataGroupingPeriods.sec15].volumeData,
          newLineChartData: sec15Data.lineData,
          newHistogramChartData: sec15Data.histogramData
        });

        periodsData.current = {
          ...periodsData.current,
          [MarketGraphChartDataGroupingPeriods.sec15]: {
            oddsData: newConcatenatedSec15Data.lineData,
            volumeData: newConcatenatedSec15Data.histogramData
          }
        };
      }

      // endregion store new points for all periods

      const { oddsData } = periodsData.current[allTimePeriod.current];

      initialRange.current = {
        ...initialRange.current,
        to: oddsData[oddsData.length - 1].time
      } as IRange<Time>;
    }
  }, [chartUpdatedPoints]);

  useEffect(() => {
    if (chartContainerRef.current && oddsLegendColorRef.current && volumeLegendColorRef.current) {
      const setInitialRange = () => {
        if (chartRef.current) {
          const currentRange = chartRef.current.timeScale().getVisibleRange();

          if (currentRange) {
            initialRange.current = currentRange;
            windowWidthRef.current = window.innerWidth;
          }
        }
      };

      const handleResize = () => {
        windowWidthRef.current = window.innerWidth;

        if (isChartZoomedRef.current && windowWidthRef.current !== window.innerWidth) {
          isChartZoomedRef.current = false;
          setIsChartZoomed(false);
        }

        if (chartRef.current && chartContainerRef.current) {
          chartRef.current.resize(chartContainerRef.current.clientWidth, chartContainerRef.current.clientHeight);

          updateInPlayMarkerPosition();

          chartRef.current.timeScale().fitContent();
        }
      };

      const { interval: initialAllTimeInterval, period: initialAllTimePeriod } = getChartInterval({
        startTimestamp: chartData[0].timestamp,
        endTimestamp: chartData[chartData.length - 1].timestamp
      });

      currentInterval.current = initialAllTimeInterval;
      allTimePeriod.current = initialAllTimePeriod;

      const { lineData, histogramData } = getLineAndHistogramData({
        chartData,
        chartSelection,
        interval: initialAllTimeInterval
      });

      const chart = createChart(
        chartContainerRef.current,
        getChartOptions({
          t,
          americanDateFormatEnabled,
          isSimplifiedView,
          isDarkTheme,
          width: chartContainerRef.current.clientWidth
        })
      );
      chartRef.current = chart;

      const oddsLegend = getComputedStyle(oddsLegendColorRef.current);
      const volumeLegend = getComputedStyle(volumeLegendColorRef.current);

      const volumeSeries = chart.addSeries(HistogramSeries, {
        color: volumeLegend.backgroundColor,
        lastValueVisible: false,
        priceFormat: {
          type: 'custom',
          formatter: (amount: number) => {
            const { formattedAmount } = getFormatValueWithCurrency({
              amount,
              settings: {
                isCheckIndian: true,
                alwaysFlexibleDisplayFormat: true,
                noRounding: true,
                convertAmountToNumber: true
              },
              t,
              displayCurrencySymbol,
              currency,
              currencySymbolAfterAmount,
              indianNumberSystemEnabled,
              replaceCurrencySymbolForMatchedAndTooltips,
              precisionType
            });

            return formattedAmount;
          }
        },
        priceLineVisible: false,
        priceScaleId: 'right' // set as an overlay by setting a blank priceScaleId
      });
      volumeSeries.priceScale().applyOptions(PRICE_SCALE_OPTIONS);

      try {
        volumeSeries.setData(histogramData);
        volumeSeriesRef.current = volumeSeries;
      } catch (error: unknown) {
        setIsChartError(true);
        // eslint-disable-next-line no-console
        console.log('initial set histogram data error', {
          error,
          histogramData,
          periodsData: periodsData.current,
          initialAllTimeInterval,
          initialAllTimePeriod,
          chartData,
          allTimePeriod: allTimePeriod.current,
          currentInterval: currentInterval.current
        });
      }

      const lineSeries = chart.addSeries(LineSeries, {
        color: oddsLegend.backgroundColor,
        ...LINE_SERIES_OPTIONS,
        priceFormat: isChanceView ? LINE_SERIES_CHANCE_PRICE_FORMAT : LINE_SERIES_ODDS_PRICE_FORMAT
      });

      try {
        lineSeries.setData(lineData);
        lineSeriesRef.current = lineSeries;
      } catch (error: unknown) {
        setIsChartError(true);
        // eslint-disable-next-line no-console
        console.log('initial set line data error', {
          error,
          lineData,
          periodsData: periodsData.current,
          initialAllTimeInterval,
          initialAllTimePeriod,
          chartData,
          allTimePeriod: allTimePeriod.current,
          currentInterval: currentInterval.current
        });
      }

      const timeScale = chart.timeScale();

      timeScale.fitContent();

      // region data-caching
      // Code below (with if-else blocks) needs for caching grouped data for different periods, to switch between them
      // while zooming without recalculations for each zoom. It's determine the biggest
      // period - initialAllTimePeriod ('All time' filter) and depends on it makes calculations for others if needed

      if (initialAllTimePeriod === MarketGraphChartDataGroupingPeriods.day1) {
        const hours2Data = getLineAndHistogramData({
          chartData,
          chartSelection,
          interval: INTERVAL_2_HOURS
        });
        const min5Data = getLineAndHistogramData({
          chartData,
          chartSelection,
          interval: INTERVAL_5_MIN
        });
        const sec15Data = getLineAndHistogramData({
          chartData,
          chartSelection,
          interval: INTERVAL_15_SEC
        });

        periodsData.current = {
          [MarketGraphChartDataGroupingPeriods.day1]: { oddsData: lineData, volumeData: histogramData },
          [MarketGraphChartDataGroupingPeriods.hour2]: {
            oddsData: hours2Data.lineData,
            volumeData: hours2Data.histogramData
          },
          [MarketGraphChartDataGroupingPeriods.min5]: {
            oddsData: min5Data.lineData,
            volumeData: min5Data.histogramData
          },
          [MarketGraphChartDataGroupingPeriods.sec15]: {
            oddsData: sec15Data.lineData,
            volumeData: sec15Data.histogramData
          }
        };
      } else if (initialAllTimePeriod === MarketGraphChartDataGroupingPeriods.hour2) {
        const min5Data = getLineAndHistogramData({
          chartData,
          chartSelection,
          interval: INTERVAL_5_MIN
        });
        const sec15Data = getLineAndHistogramData({
          chartData,
          chartSelection,
          interval: INTERVAL_15_SEC
        });

        periodsData.current = {
          ...periodsData.current,
          [MarketGraphChartDataGroupingPeriods.hour2]: { oddsData: lineData, volumeData: histogramData },
          [MarketGraphChartDataGroupingPeriods.min5]: {
            oddsData: min5Data.lineData,
            volumeData: min5Data.histogramData
          },
          [MarketGraphChartDataGroupingPeriods.sec15]: {
            oddsData: sec15Data.lineData,
            volumeData: sec15Data.histogramData
          }
        };
      } else if (initialAllTimePeriod === MarketGraphChartDataGroupingPeriods.min5) {
        const sec15Data = getLineAndHistogramData({
          chartData,
          chartSelection,
          interval: INTERVAL_15_SEC
        });

        periodsData.current = {
          ...periodsData.current,
          [MarketGraphChartDataGroupingPeriods.min5]: { oddsData: lineData, volumeData: histogramData },
          [MarketGraphChartDataGroupingPeriods.sec15]: {
            oddsData: sec15Data.lineData,
            volumeData: sec15Data.histogramData
          }
        };
      } else if (initialAllTimePeriod === MarketGraphChartDataGroupingPeriods.sec15) {
        periodsData.current = {
          ...periodsData.current,
          [MarketGraphChartDataGroupingPeriods.sec15]: { oddsData: lineData, volumeData: histogramData }
        };
      }

      setTimeout(setInitialRange, 200);

      // endregion data-caching

      let prevBarSpacing: number | null = null;
      let prevPeriod = initialAllTimePeriod;

      setTimeout(() => {
        timeScale.subscribeVisibleTimeRangeChange(range => {
          if (!initialRange.current || !range || window.innerWidth !== windowWidthRef.current) return;

          const { oddsData } = periodsData.current[IntervalsPeriodsMap[currentInterval.current]];

          if (range.from === oddsData[0].time) {
            if (!isToolbarPanLeftDisabledRef.current) {
              isToolbarPanLeftDisabledRef.current = true;
              setIsToolbarPanLeftDisabled(true);
            }
          } else {
            if (isToolbarPanLeftDisabledRef.current) {
              isToolbarPanLeftDisabledRef.current = false;
              setIsToolbarPanLeftDisabled(false);
            }
          }

          if (range.to === oddsData[oddsData.length - 1].time) {
            if (!isToolbarPanRightDisabledRef.current) {
              isToolbarPanRightDisabledRef.current = true;
              setIsToolbarPanRightDisabled(true);
            }
          } else {
            if (isToolbarPanRightDisabledRef.current) {
              isToolbarPanRightDisabledRef.current = false;
              setIsToolbarPanRightDisabled(false);
            }
          }

          updateInPlayMarkerPosition();

          const isSameRange =
            Math.round(initialRange.current.from as number) === Math.round(range.from as number) &&
            Math.round(initialRange.current.to as number) === Math.round(range.to as number);

          if (isChartZoomedRef.current !== !isSameRange && !isSimplifiedViewChanged.current) {
            isChartZoomedRef.current = !isSameRange;
            setIsChartZoomed(!isSameRange);
          }

          if (isSimplifiedViewChanged.current) {
            isSimplifiedViewChanged.current = false;
          }

          if (range && !filterSelectionInProcess.current) {
            const currentBarSpacing = timeScale.options().barSpacing;
            const rangeDiffInMilliseconds = ((range.to as number) - (range.from as number)) * 1000;
            let newPeriod: MarketGraphChartDataGroupingPeriod;

            if (rangeDiffInMilliseconds > ONE_MONTH_IN_MILLISECONDS) {
              newPeriod = MarketGraphChartDataGroupingPeriods.day1;
            } else if (
              rangeDiffInMilliseconds > INTERVAL_1_DAY &&
              rangeDiffInMilliseconds <= ONE_MONTH_IN_MILLISECONDS
            ) {
              newPeriod = MarketGraphChartDataGroupingPeriods.hour2;
            } else if (
              rangeDiffInMilliseconds > ONE_HOUR_IN_MILLISECONDS &&
              rangeDiffInMilliseconds <= INTERVAL_1_DAY
            ) {
              newPeriod = MarketGraphChartDataGroupingPeriods.min5;
            } else {
              newPeriod = MarketGraphChartDataGroupingPeriods.sec15;
            }

            if (prevBarSpacing === null) {
              prevBarSpacing = currentBarSpacing;
              return;
            }

            if (currentBarSpacing !== prevBarSpacing) {
              prevBarSpacing = currentBarSpacing;

              if (selectedFilterRef.current !== MarketGraphChartFilters.custom && !isResetClicked.current) {
                selectedFilterRef.current = MarketGraphChartFilters.custom;
                setSelectedFilter(MarketGraphChartFilters.custom);
              }

              if (isResetClicked.current) {
                setTimeout(() => {
                  isResetClicked.current = false;
                }, 100);
              }
            }

            if (prevPeriod !== newPeriod && !!periodsData.current[newPeriod].oddsData && !isResetClicked.current) {
              currentInterval.current = PeriodsIntervalsMap[newPeriod];
              prevPeriod = newPeriod;

              try {
                lineSeries.setData(periodsData.current[newPeriod].oddsData);
                volumeSeries.setData(periodsData.current[newPeriod].volumeData);
              } catch (error: unknown) {
                setIsChartError(true);
                // eslint-disable-next-line no-console
                console.log('set line and histogram chart in zoom error', {
                  error,
                  periodsData: periodsData.current,
                  newPeriod,
                  chartData,
                  allTimePeriod: allTimePeriod.current,
                  currentInterval: currentInterval.current
                });
              }
            }
          }
        });
      }, 100);

      window.addEventListener('resize', handleResize);

      return () => {
        window.removeEventListener('resize', handleResize);
        chart.remove();
      };
    }
  }, []);

  useEffect(() => {
    if (marketStartTime && chartRef.current && isMarketInPlay) {
      setTimeout(() => {
        updateInPlayMarkerPosition();
      }, 200);
    }
  }, [marketStartTime, isMarketInPlay]);

  useEffect(() => {
    // There is some delay in height recalculation when switching from mobile to desktop
    setTimeout(() => {
      if (chartRef.current && chartContainerRef.current) {
        chartRef.current?.applyOptions({
          height: chartContainerRef.current?.clientHeight
        });
      }
    }, 100);
  }, [isMobile, headerHeight, totalMatchedBlockHeight]);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
    } else if (chartContainerRef.current) {
      chartRef.current?.applyOptions({ width: chartContainerRef.current.clientWidth });
      isSimplifiedViewChanged.current = true;
    }
  }, [isSimplifiedView]);

  const panChart = (direction: 'left' | 'right') => {
    if (!chartRef.current) return;
    const timeScale = chartRef.current.timeScale();
    const visibleRange = timeScale.getVisibleRange();
    const scrollPosition = timeScale.scrollPosition();

    if (visibleRange?.from !== undefined && visibleRange.to !== undefined) {
      const shift = direction === 'left' ? -10 : 10; // Shift by 10 index

      timeScale.scrollToPosition(scrollPosition + shift, false);
    }
  };

  const zoomChart = (zoomIn: boolean) => {
    if (!chartRef.current) return;
    const timeScale = chartRef.current.timeScale();
    const currentSpacing = timeScale.options().barSpacing;
    timeScale.applyOptions({
      barSpacing: zoomIn ? currentSpacing * 1.2 : currentSpacing * 0.8 // Increase/decrease spacing
    });
  };

  const resetChart = () => {
    if (!chartRef.current || !initialRange.current || !lineSeriesRef.current || !volumeSeriesRef.current) return;
    const timeScale = chartRef.current.timeScale();
    isResetClicked.current = true;

    const currentPeriod = IntervalsPeriodsMap[currentInterval.current];

    // Reset pan (visible range)
    timeScale.setVisibleRange({
      from: initialRange.current.from,
      to: initialRange.current.to
    });
    if (allTimePeriod.current !== currentPeriod) {
      currentInterval.current = PeriodsIntervalsMap[allTimePeriod.current];
      lineSeriesRef.current.setData(periodsData.current[allTimePeriod.current].oddsData);
      volumeSeriesRef.current.setData(periodsData.current[allTimePeriod.current].volumeData);
    }

    timeScale.fitContent();
    selectedFilterRef.current = MarketGraphChartFilters.all;
    setSelectedFilter(MarketGraphChartFilters.all);
  };

  const handleSelectFilter = (newFilter: MarketGraphChartFilter) => {
    if (selectedFilter !== newFilter && lineSeriesRef.current && volumeSeriesRef.current && chartRef.current) {
      filterSelectionInProcess.current = true;
      selectedFilterRef.current = newFilter;
      setSelectedFilter(newFilter);

      let period: MarketGraphChartDataGroupingPeriod | undefined;

      if (newFilter === MarketGraphChartFilters.day) {
        if (periodsData.current.min5.oddsData.length) {
          period = MarketGraphChartDataGroupingPeriods.min5;
          currentInterval.current = INTERVAL_5_MIN;
        } else {
          period = MarketGraphChartDataGroupingPeriods.sec15;
          currentInterval.current = INTERVAL_15_SEC;
        }
      } else if (newFilter === MarketGraphChartFilters.hour) {
        currentInterval.current = INTERVAL_15_SEC;
        period = MarketGraphChartDataGroupingPeriods.sec15;
      } else if (newFilter === MarketGraphChartFilters.inPlay) {
        if (marketStartTime) {
          const diff = chartData[chartData.length - 1].timestamp - lastTimestamp;

          if (diff >= ONE_MONTH_IN_MILLISECONDS) {
            if (periodsData.current.day1.oddsData.length) {
              period = MarketGraphChartDataGroupingPeriods.day1;
              currentInterval.current = INTERVAL_1_DAY;
            } else if (periodsData.current.hour2.oddsData.length) {
              period = MarketGraphChartDataGroupingPeriods.hour2;
              currentInterval.current = INTERVAL_2_HOURS;
            } else if (periodsData.current.min5.oddsData.length) {
              period = MarketGraphChartDataGroupingPeriods.min5;
              currentInterval.current = INTERVAL_5_MIN;
            } else {
              period = MarketGraphChartDataGroupingPeriods.sec15;
              currentInterval.current = INTERVAL_15_SEC;
            }
          } else if (diff >= INTERVAL_1_DAY) {
            if (periodsData.current.hour2.oddsData.length) {
              period = MarketGraphChartDataGroupingPeriods.hour2;
              currentInterval.current = INTERVAL_2_HOURS;
            } else if (periodsData.current.min5.oddsData.length) {
              period = MarketGraphChartDataGroupingPeriods.min5;
              currentInterval.current = INTERVAL_5_MIN;
            } else {
              period = MarketGraphChartDataGroupingPeriods.sec15;
              currentInterval.current = INTERVAL_15_SEC;
            }
          } else if (diff >= ONE_HOUR_IN_MILLISECONDS) {
            if (periodsData.current.min5.oddsData.length) {
              period = MarketGraphChartDataGroupingPeriods.min5;
              currentInterval.current = INTERVAL_5_MIN;
            } else {
              period = MarketGraphChartDataGroupingPeriods.sec15;
              currentInterval.current = INTERVAL_15_SEC;
            }
          } else {
            period = MarketGraphChartDataGroupingPeriods.sec15;
            currentInterval.current = INTERVAL_15_SEC;
          }
        }
      } else {
        if (periodsData.current.day1.oddsData.length) {
          period = MarketGraphChartDataGroupingPeriods.day1;
          currentInterval.current = INTERVAL_1_DAY;
        } else if (periodsData.current.hour2.oddsData.length) {
          period = MarketGraphChartDataGroupingPeriods.hour2;
          currentInterval.current = INTERVAL_2_HOURS;
        } else if (periodsData.current.min5.oddsData.length) {
          period = MarketGraphChartDataGroupingPeriods.min5;
          currentInterval.current = INTERVAL_5_MIN;
        } else {
          period = MarketGraphChartDataGroupingPeriods.sec15;
          currentInterval.current = INTERVAL_15_SEC;
        }
      }

      if (period) {
        try {
          lineSeriesRef.current.setData(periodsData.current[period].oddsData);
          volumeSeriesRef.current.setData(periodsData.current[period].volumeData);

          if (newFilter === MarketGraphChartFilters.inPlay) {
            if (marketStartTime) {
              chartRef.current.timeScale().setVisibleRange({
                from: (marketStartTime / 1000) as UTCTimestamp,
                to: (lastTimestamp / 1000) as UTCTimestamp
              });
            }
          } else {
            if (period === MarketGraphChartDataGroupingPeriods.min5) {
              chartRef.current.timeScale().setVisibleRange({
                from: ((lastTimestamp - INTERVAL_1_DAY) / 1000) as UTCTimestamp,
                to: (lastTimestamp / 1000) as UTCTimestamp
              });
            } else if (period === MarketGraphChartDataGroupingPeriods.sec15) {
              chartRef.current.timeScale().setVisibleRange({
                from: ((lastTimestamp - ONE_HOUR_IN_MILLISECONDS) / 1000) as UTCTimestamp,
                to: (lastTimestamp / 1000) as UTCTimestamp
              });
            }
          }
        } catch (error: unknown) {
          setIsChartError(true);
          // eslint-disable-next-line no-console
          console.log('set data on select new filter error', {
            error,
            periodsData: periodsData.current,
            chartData,
            newFilter,
            currentInterval: currentInterval.current,
            allTimePeriod: allTimePeriod.current
          });
        }

        if (newFilter === MarketGraphChartFilters.all) {
          chartRef.current.timeScale().fitContent();
        }

        setTimeout(() => {
          filterSelectionInProcess.current = false;
        }, 100);
      }
    }
  };

  const handleChangeDesktopSelection = (newSelection: MarketGraphChartSelection) => {
    if (newSelection !== chartSelection) {
      searchParams.set(MARKET_GRAPH_SELECTION_PARAM_NAME, newSelection);
      setSearchParams(searchParams, { replace: true });
      setIsOpenedDropdown(false);

      if (lineSeriesRef.current && volumeSeriesRef.current) {
        const prevInterval = currentInterval.current;
        const newInterval = PeriodsIntervalsMap[allTimePeriod.current];

        const newLineChartData = convertLineChartDataForSelection({
          data: periodsData.current[allTimePeriod.current].oddsData
        });
        currentInterval.current = PeriodsIntervalsMap[allTimePeriod.current];

        if (selectedFilter !== MarketGraphChartFilters.all) {
          selectedFilterRef.current = MarketGraphChartFilters.all;
          setSelectedFilter(MarketGraphChartFilters.all);
        }

        resetChart();

        if (newSelection === ChartSelections.chance) {
          lineSeriesRef.current?.applyOptions({
            priceFormat:
              newSelection === ChartSelections.chance ? LINE_SERIES_CHANCE_PRICE_FORMAT : LINE_SERIES_ODDS_PRICE_FORMAT
          });
        }

        try {
          lineSeriesRef.current.setData(newLineChartData);

          if (prevInterval !== newInterval) {
            volumeSeriesRef.current.setData(periodsData.current[allTimePeriod.current].volumeData);
          }
        } catch (error: unknown) {
          setIsChartError(true);
          // eslint-disable-next-line no-console
          console.log('set line data on change desktop selection error', {
            error,
            chartSelection,
            newSelection,
            newLineChartData,
            chartData,
            periodsData: periodsData.current,
            allTimePeriod: allTimePeriod.current,
            currentInterval: currentInterval.current
          });
        }

        // region update cached data
        // Code below (with if-else blocks) needs for update cached grouped data for different periods with new data for
        // line on chart according to new selection (above in useEffect where chart creates there is data-caching block)
        if (allTimePeriod.current === MarketGraphChartDataGroupingPeriods.day1) {
          const newHours2LineChartData = convertLineChartDataForSelection({
            data: periodsData.current[MarketGraphChartDataGroupingPeriods.hour2].oddsData
          });
          const newMin5LineChartData = convertLineChartDataForSelection({
            data: periodsData.current[MarketGraphChartDataGroupingPeriods.min5].oddsData
          });
          const newSec15LineChartData = convertLineChartDataForSelection({
            data: periodsData.current[MarketGraphChartDataGroupingPeriods.sec15].oddsData
          });

          periodsData.current = {
            [MarketGraphChartDataGroupingPeriods.day1]: {
              ...periodsData.current[MarketGraphChartDataGroupingPeriods.day1],
              oddsData: newLineChartData
            },
            [MarketGraphChartDataGroupingPeriods.hour2]: {
              ...periodsData.current[MarketGraphChartDataGroupingPeriods.hour2],
              oddsData: newHours2LineChartData
            },
            [MarketGraphChartDataGroupingPeriods.min5]: {
              ...periodsData.current[MarketGraphChartDataGroupingPeriods.min5],
              oddsData: newMin5LineChartData
            },
            [MarketGraphChartDataGroupingPeriods.sec15]: {
              ...periodsData.current[MarketGraphChartDataGroupingPeriods.sec15],
              oddsData: newSec15LineChartData
            }
          };
        } else if (allTimePeriod.current === MarketGraphChartDataGroupingPeriods.hour2) {
          const newMin5LineChartData = convertLineChartDataForSelection({
            data: periodsData.current[MarketGraphChartDataGroupingPeriods.min5].oddsData
          });
          const newSec15LineChartData = convertLineChartDataForSelection({
            data: periodsData.current[MarketGraphChartDataGroupingPeriods.sec15].oddsData
          });

          periodsData.current = {
            ...periodsData.current,
            [MarketGraphChartDataGroupingPeriods.hour2]: {
              ...periodsData.current[MarketGraphChartDataGroupingPeriods.hour2],
              oddsData: newLineChartData
            },
            [MarketGraphChartDataGroupingPeriods.min5]: {
              ...periodsData.current[MarketGraphChartDataGroupingPeriods.min5],
              oddsData: newMin5LineChartData
            },
            [MarketGraphChartDataGroupingPeriods.sec15]: {
              ...periodsData.current[MarketGraphChartDataGroupingPeriods.sec15],
              oddsData: newSec15LineChartData
            }
          };
        } else if (allTimePeriod.current === MarketGraphChartDataGroupingPeriods.min5) {
          const newSec15LineChartData = convertLineChartDataForSelection({
            data: periodsData.current[MarketGraphChartDataGroupingPeriods.sec15].oddsData
          });

          periodsData.current = {
            ...periodsData.current,
            [MarketGraphChartDataGroupingPeriods.min5]: {
              ...periodsData.current[MarketGraphChartDataGroupingPeriods.min5],
              oddsData: newLineChartData
            },
            [MarketGraphChartDataGroupingPeriods.sec15]: {
              ...periodsData.current[MarketGraphChartDataGroupingPeriods.sec15],
              oddsData: newSec15LineChartData
            }
          };
        } else if (allTimePeriod.current === MarketGraphChartDataGroupingPeriods.sec15) {
          periodsData.current = {
            ...periodsData.current,
            [MarketGraphChartDataGroupingPeriods.sec15]: {
              ...periodsData.current[MarketGraphChartDataGroupingPeriods.sec15],
              oddsData: newLineChartData
            }
          };
        }
        // endregion update cached data
      }
    }
  };

  const handleClickChartSelection = () => {
    if (isTabletOrMobileDevice) {
      navigate(
        `${MARKET_GRAPH_LINE_CHART_SELECTION_BASE_URL}/${marketId}/${selectionId}/${handicap}/${chartSelection}`,
        { replace: true }
      );
    } else {
      setIsOpenedDropdown(prevState => !prevState);
    }
  };

  const getLeftLegendLabel = () => {
    if (isChanceView) {
      return t('marketGraph.labels.impliedChance');
    }

    if (isLineMarket && marketUnit) {
      return marketUnitTranslated;
    }

    return t('account.mybets.labels.odds');
  };

  const toolbarItems = useMemo(() => {
    if (isTabletOrMobileDevice) {
      return [];
    }

    return [
      {
        icon: 'biab_custom-icon-chevron-left-2',
        disabled: !isChartZoomed || isToolbarPanLeftDisabled,
        onClick: () => panChart('left'),
        id: 'pan-left'
      },
      { icon: 'biab_custom-icon-minus', disabled: !isChartZoomed, onClick: () => zoomChart(false), id: 'zoom-out' },
      { icon: 'biab_custom-icon-refresh', disabled: !isChartZoomed, onClick: resetChart, id: 'reset' },
      { icon: 'biab_custom-icon-plus', disabled: false, onClick: () => zoomChart(true), id: 'zoom-in' },
      {
        icon: 'biab_custom-icon-chevron-right-2',
        disabled: !isChartZoomed || isToolbarPanRightDisabled,
        onClick: () => panChart('right'),
        id: 'pan-right'
      }
    ];
  }, [isChartZoomed, isToolbarPanLeftDisabled, isToolbarPanRightDisabled]);

  useEffect(() => {
    if (!isTabletOrMobileDevice) {
      const keyboardHandler = (event: KeyboardEvent) => {
        if (
          (event.metaKey || event.ctrlKey || (event.metaKey && event.altKey) || (event.ctrlKey && event.altKey)) &&
          (event.key === 'r' || event.code === 'KeyR' || event.key === '®')
        ) {
          event.preventDefault();

          if (isChartZoomedRef.current) {
            resetChart();
          }
        }

        if (event.key === 'ArrowLeft') {
          if (isChartZoomedRef.current && !isToolbarPanLeftDisabledRef.current) {
            panChart('left');
          }
        } else if (event.key === 'ArrowRight') {
          if (isChartZoomedRef.current && !isToolbarPanRightDisabledRef.current) {
            panChart('right');
          }
        } else if (
          (event.ctrlKey || event.metaKey) &&
          (event.key === '+' || event.key === '=' || event.code === 'NumpadAdd')
        ) {
          event.preventDefault();
          zoomChart(true);
        } else if ((event.ctrlKey || event.metaKey) && (event.key === '-' || event.code === 'NumpadSubtract')) {
          event.preventDefault();
          if (isChartZoomedRef.current) {
            zoomChart(false);
          }
        }
      };

      window.addEventListener('keydown', keyboardHandler);

      return () => {
        window.removeEventListener('keydown', keyboardHandler);
      };
    }
  }, []);

  return (
    <>
      <div className={styles.filters__container}>
        <div
          className={classNames(styles.filters, {
            [styles.filters__dark]: isDarkTheme
          })}
        >
          {filters.map(({ label, value }, index) => {
            const isActive = selectedFilter === value;

            return (
              <button
                key={value}
                tabIndex={index + 1 + MARKET_GRAPH_LIQUIDITY_TABLE_TAB_INDEX}
                data-active={isActive}
                className={classNames(styles.filters__item, {
                  [styles.filters__item__mobile]: isTabletOrMobileDevice,
                  [styles.filters__item__active]: isActive,
                  [styles.filters__item__dark]: isDarkTheme,
                  [styles.filters__item__active__dark]: isActive && isDarkTheme
                })}
                onClick={() => handleSelectFilter(value)}
              >
                {label}
              </button>
            );
          })}
        </div>
        {!isLineMarket && (
          <div className={styles.toggle__container} ref={dropdownRef}>
            <button
              tabIndex={-1}
              onClick={handleClickChartSelection}
              data-open={isTabletOrMobileDevice ? undefined : isOpenedDropdown}
              className={classNames(styles.toggle, {
                [styles.toggle__mobile]: isTabletOrMobileDevice,
                [styles.toggle__opened]: isOpenedDropdown,
                [styles.toggle__dark]: isDarkTheme,
                [styles.toggle__opened__dark]: isOpenedDropdown && isDarkTheme
              })}
            >
              <i
                className={classNames('biab_custom-icon-chart', marketGraphsBranding.ICON, styles.toggle__icon, {
                  [styles.toggle__icon__mobile]: isTabletOrMobileDevice,
                  [styles.toggle__icon__dark]: isDarkTheme
                })}
              />
            </button>
            {!isTabletOrMobileDevice && (
              <div
                className={classNames(styles.dropdown, {
                  [styles.dropdown__opened]: isOpenedDropdown,
                  [styles.dropdown__closed]: !isOpenedDropdown,
                  [styles.dropdown__dark]: isDarkTheme
                })}
              >
                {chartSelections.map(({ label, value }) => {
                  const isSelected = value === chartSelection;

                  return (
                    <button
                      key={value}
                      tabIndex={-1}
                      className={classNames(styles.dropdown__item, {
                        [styles.dropdown__item__selected]: isSelected,
                        [styles.dropdown__item__dark]: isDarkTheme
                      })}
                      onClick={() => handleChangeDesktopSelection(value)}
                    >
                      {label}
                      {isSelected && (
                        <i
                          className={classNames('biab_custom-icon-checkmark', styles.dropdown__item__icon, {
                            [styles.dropdown__item__icon__dark]: isDarkTheme
                          })}
                        />
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
      <div
        className={classNames(styles.legendAndChart, {
          [styles.legendAndChart__mobile]: isTabletOrMobileDevice
        })}
      >
        <div className={styles.legend}>
          <div className={styles.legend__block}>
            <p
              className={classNames(styles.legend__text, {
                [styles.legend__text__mobile]: isTabletOrMobileDevice,
                [styles.legend__text__dark]: isDarkTheme
              })}
            >
              {getLeftLegendLabel()}
            </p>
            <div
              id="odds-legend-color"
              className={classNames(styles.legend__line, marketGraphsBranding.LINE_CHART, {
                [styles.legend__line__dark]: isDarkTheme
              })}
              ref={oddsLegendColorRef}
            />
          </div>
          <div className={styles.legend__block}>
            <div
              id="volume-legend-color"
              className={classNames(styles.legend__square, marketGraphsBranding.VOLUME_BAR, {
                [styles.legend__square__dark]: isDarkTheme
              })}
              ref={volumeLegendColorRef}
            />
            <p
              className={classNames(styles.legend__text, {
                [styles.legend__text__mobile]: isTabletOrMobileDevice,
                [styles.legend__text__dark]: isDarkTheme
              })}
            >
              {t('marketGraph.labels.volume')}
            </p>
          </div>
        </div>
        <div
          className={classNames(styles.chartContainer, {
            [styles.chartContainer__simplified__mobile]: isMobile && isSimplifiedView
          })}
        >
          <div
            ref={chartContainerRef}
            className={classNames(styles.chart, {
              [styles.chart__mobile]: isMobile,
              [styles.chart__simplified]: !isMobile && isSimplifiedView,
              [styles.chart__simplified__mobile]: isMobile && isSimplifiedView
            })}
            style={
              isMobile
                ? undefined
                : {
                    maxHeight: `${
                      minWindowHeight - headerHeight - totalMatchedBlockHeight - DESKTOP_STATIC_ELEMENTS_HEIGHT
                    }px`
                  }
            }
          >
            <p
              ref={inPlayMarkerTextRef}
              className={classNames(styles.chart__inPlayMarker__text, marketGraphsBranding.IN_PLAY_INDICATOR, {
                hidden: !marketStartTime || !isMarketInPlay
              })}
            >
              {t('market.inPlay')}
            </p>
            <div
              ref={inPlayMarkerLineRef}
              className={classNames(styles.chart__inPlayMarker__line, marketGraphsBranding.IN_PLAY_INDICATOR, {
                hidden: !marketStartTime || !isMarketInPlay
              })}
            />
          </div>
          {!isMobile && (
            <div className={classNames(styles.toolbar, { [styles.toolbar__dark]: isDarkTheme })}>
              {toolbarItems.map(({ icon, id, onClick, disabled }, index) => {
                return (
                  <button
                    key={id}
                    tabIndex={index + 1 + MARKET_GRAPH_LIQUIDITY_TABLE_TAB_INDEX + filters.length}
                    data-action={id}
                    className={classNames(styles.toolbar__item, { [styles.toolbar__item__dark]: isDarkTheme })}
                    disabled={disabled}
                    onClick={onClick}
                  >
                    <i
                      className={classNames(icon, styles.toolbar__item__icon, {
                        [styles.toolbar__item__icon__disabled]: disabled,
                        [styles.toolbar__item__icon__dark]: isDarkTheme,
                        [styles.toolbar__item__icon__disabled__dark]: isDarkTheme && disabled
                      })}
                    />
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
